<template>
  <div class="modern-sales-notes-view pb-12">
    <!-- Premium Global Header (Master Pattern) -->
    <v-row class="mb-6 align-center px-4 pt-4">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="44" class="mr-4 shadow-sm border-white-sm">
          <v-icon color="white" small>fas fa-handshake</v-icon>
        </v-avatar>
        <div>
          <div class="text-h4 font-weight-black primary--text d-flex align-center lh-1">
            Cotizaciones
          </div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">Seguimiento y gestión de presupuestos comerciales</div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <div class="d-flex align-center">
        <v-btn 
          v-if="$can('configure', 'Sales')"
          to="sales/configuration" 
          text
          color="primary"
          class="font-weight-black text-uppercase tracking-widest mr-2 rounded-lg"
        >
          <v-icon left size="14">fas fa-cog</v-icon>
          Mantenedores
        </v-btn>
        
        <v-btn
          v-if="$can('read', 'Calendar')"
          @click="$router.push({ name: 'CompromiseCalendar2' })"
          text
          color="primary"
          class="font-weight-black text-uppercase tracking-widest mr-2 rounded-lg"
        >
          <v-icon left size="14">fas fa-calendar-alt</v-icon>
          Calendario
        </v-btn>

        <export-sales-notes
          :filter="filter"
          :options="options"
          class="mr-3"
        />
        
        <v-btn 
          color="accent" 
          class="font-weight-black text-uppercase tracking-widest rounded-lg px-6 h-44 shadow-sm"
          @click="create"
        >
          <v-icon left size="14">fas fa-plus</v-icon>
          Nueva
        </v-btn>
      </div>
    </v-row>

    <!-- Bespoke Enterprise Metric Cards (Root Level Performance) -->
    <v-row dense class="mb-8">
      <v-col cols="12" sm="4">
        <v-card class="bespoke-mini-card flat-border h-100" flat>
          <div class="metric-accent blue-accent"></div>
          <v-card-text class="pa-4 text-center px-6">
            <div class="metric-label">Cotizado Total</div>
            <div class="text-h5 font-weight-black primary--text tracking-tighter">{{ quoted | moneda }}</div>
            <div class="caption grey--text font-weight-bold mt-1 uppercase tracking-tight">Potencial del período</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4">
        <v-card class="bespoke-mini-card flat-border h-100" flat>
          <div class="metric-accent success-accent"></div>
          <v-card-text class="pa-4 text-center px-6">
            <div class="metric-label">Confirmado / Ganado</div>
            <div class="d-flex align-baseline gap-2 justify-center">
              <span class="text-h5 font-weight-black success--text tracking-tighter">{{ confirmed | moneda }}</span>
              <v-chip x-small color="success lighten-5" class="success--text font-weight-black">
                {{ (confirmed / quoted) | percent }}
              </v-chip>
            </div>
            <div class="caption grey--text font-weight-bold mt-1 uppercase tracking-tight">Conversión comercial</div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" sm="4">
        <v-card class="bespoke-mini-card flat-border h-100" flat>
          <div class="metric-accent red-accent"></div>
          <v-card-text class="pa-4 text-center px-6">
            <div class="metric-label">Cerrado / Rechazado</div>
            <div class="d-flex align-baseline gap-2 justify-center">
              <span class="text-h5 font-weight-black red--text tracking-tighter">{{ rejected | moneda }}</span>
              <v-chip x-small color="error lighten-5" class="error--text font-weight-black">
                {{ (rejected / quoted) | percent }}
              </v-chip>
            </div>
            <div class="caption grey--text font-weight-bold mt-1 uppercase tracking-tight">Oportunidades perdidas</div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Unified Filter Card -->
    <v-card outlined class="modern-card pa-6 mb-8 shadow-sm">
      <v-row dense>
        <v-col cols="12" md="3">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Fecha Creación</div>
          <v-row dense>
            <v-col cols="6">
              <v-text-field v-model="filter.created_after" outlined dense hide-details="auto" type="date" label="Desde" class="modern-input" />
            </v-col>
            <v-col cols="6">
              <v-text-field v-model="filter.created_before" outlined dense hide-details="auto" type="date" label="Hasta" class="modern-input" />
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" md="3">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Probable Cierre</div>
          <v-row dense>
            <v-col cols="6">
              <v-text-field v-model="filter.closing_after" outlined dense hide-details="auto" type="date" label="Desde" class="modern-input" />
            </v-col>
            <v-col cols="6">
              <v-text-field v-model="filter.closing_before" outlined dense hide-details="auto" type="date" label="Hasta" class="modern-input" />
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" md="6">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Entidad / Cliente</div>
          <customer-complete2 v-model="filter.customer" />
        </v-col>
      </v-row>
      
      <v-row dense class="mt-4">
        <v-col cols="12" md="4">
          <Can I="readAll" a="SalesNote">
            <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Responsable Comercial</div>
            <v-select
              v-model="filter.seller"
              :items="sellers"
              item-text="email"
              item-value="id"
              outlined dense hide-details="auto"
              clearable
              placeholder="Todos los vendedores"
              class="modern-select"
            />
          </Can>
        </v-col>
        <v-col cols="12" md="4">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Estado Presupuesto</div>
          <v-select
            v-model="filter.status"
            :items="statusOptions"
            item-text="text"
            item-value="value"
            outlined dense hide-details="auto"
            clearable
            placeholder="Todos los estados"
            class="modern-select"
          />
        </v-col>
        <v-col cols="12" md="4">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Origen / Campaña</div>
          <OriginSelection v-model="filter.origin" :filtro="true" />
        </v-col>
      </v-row>
    </v-card>

    <!-- Main Results Section -->
    <v-card outlined class="modern-card overflow-hidden shadow-sm">
      <v-data-table
        :headers="headers"
        :items="salesNotes"
        :options.sync="options"
        :server-items-length="totalSalesNotes"
        :loading="loading"
        class="modern-table"
        :footer-props="{
          showFirstLastPage: true,
          itemsPerPageOptions: [10, 20, 50, 100],
        }"
        @click:row="showSalesNote"
      >
        <!-- Search Bar in Top Slot -->
        <template v-slot:top>
          <div class="px-6 py-4 border-bottom d-flex align-center bg-white">
            <v-text-field
              v-model="search"
              placeholder="Buscar por Número, Cliente o Proyecto..."
              prepend-inner-icon="fas fa-search"
              outlined dense hide-details="auto"
              clearable
              :disabled="loading"
              class="modern-search-field flex-grow-1"
              @keyup="searchTimeOut"
            />
          </div>
        </template>

        <template v-slot:item.id="{ item }">
          <div class="d-flex align-center pointer-cursor" @click.stop="showSalesNote(item)">
            <div class="d-flex flex-column">
              <span class="font-weight-black primary--text body-2 bespoke-link">#{{ item.id }}</span>
              <div class="d-flex align-center mt-1 gap-1">
                <v-tooltip bottom v-if="item.status === 'rejected'">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon x-small v-bind="attrs" v-on="on" color="error">fas fa-times-circle</v-icon>
                  </template>
                  <div class="caption pa-2">
                    <div class="font-weight-bold">Rechazo: {{ item.rejected_reason === 'price' ? 'Precio' : 'Fecha Entrega' }}</div>
                    <div>{{ item.rejected_at | moment("DD/MM/YYYY HH:mm") }}</div>
                    <div class="mt-1 font-italic">{{ item.rejected_note }}</div>
                  </div>
                </v-tooltip>
                <v-tooltip bottom v-if="item.status === 'confirmed'">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon x-small v-bind="attrs" v-on="on" color="success">fas fa-check-circle</v-icon>
                  </template>
                  <span>Negocio Cerrado / Ganado</span>
                </v-tooltip>
                <v-tooltip bottom v-if="item.status === 'pending'">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon x-small v-bind="attrs" v-on="on" color="orange">fas fa-clock</v-icon>
                  </template>
                  <span>Pendiente de Confirmación</span>
                </v-tooltip>
              </div>
            </div>
          </div>
        </template>

        <template v-slot:item.created_at="{ item }">
          <div class="d-flex flex-column">
            <span class="body-2 grey--text text--darken-3 font-weight-medium">
              {{ item.created_at | moment("DD/MM/YYYY") }}
            </span>
            <span class="caption grey--text italic">{{ fromNow(item.created_at) }}</span>
          </div>
        </template>

        <template v-slot:item.name="{ item }">
          <div class="body-2 font-weight-bold grey--text text--darken-4 text-truncate" style="max-width: 250px;">
            {{ item.name }}
          </div>
          <div v-if="item.origin" class="d-flex align-center mt-1">
            <v-chip x-small outlined color="grey" class="caption px-1 font-weight-bold" disabled>
              {{ item.origin_name }}
            </v-chip>
          </div>
        </template>

        <template v-slot:item.closing_date="{ item }">
          <div class="d-flex flex-column">
            <span class="body-2 font-weight-bold" :class="isUrgent(item) ? 'error--text' : 'grey--text text--darken-2'">
              {{ item.closing_date | moment("DD/MM/YYYY") }}
            </span>
            <span class="caption grey--text">Fecha estimada</span>
          </div>
        </template>

        <template v-slot:item.customer__name="{ item }">
          <div class="body-2 font-weight-black primary--text">{{ item.sales_note_customer.name }}</div>
          <div class="caption grey--text mt-1 d-flex align-center">
            <v-icon x-small class="mr-1" color="grey lighten-1">fas fa-building</v-icon>
            {{ item.sales_note_customer.rut }}
          </div>
        </template>

        <template v-slot:item.customer__seller__first_name="{ item }">
          <div class="d-flex align-center">
            <v-avatar size="24" color="primary lighten-4" class="mr-2">
              <span class="caption font-weight-black primary--text">{{ item.sales_note_customer.seller.first_name[0] }}{{ item.sales_note_customer.seller.last_name[0] }}</span>
            </v-avatar>
            <div class="d-flex flex-column">
              <span class="body-2 font-weight-medium">{{ item.sales_note_customer.seller.first_name }} {{ item.sales_note_customer.seller.last_name }}</span>
            </div>
          </div>
        </template>

        <template v-slot:item.total="{ item }">
          <div class="d-flex flex-column align-end">
            <div class="text-subtitle-1 font-weight-black primary--text tracking-tighter">
              {{ item.totals.total | moneda }}
            </div>
            <div class="d-flex align-center gap-1 mt-1">
              <v-tooltip bottom v-if="item.flag">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon x-small v-bind="attrs" v-on="on" color="error">fas fa-exclamation-triangle</v-icon>
                </template>
                <span class="caption">Alerta: Ítems por debajo del costo directo</span>
              </v-tooltip>
              <v-tooltip bottom v-if="item.yellow_flag">
                <template v-slot:activator="{ on, attrs }">
                  <v-icon x-small v-bind="attrs" v-on="on" color="orange darken-1">fas fa-exclamation-triangle</v-icon>
                </template>
                <span class="caption">Margen por debajo del objetivo original</span>
              </v-tooltip>
            </div>
          </div>
        </template>

        <template v-slot:item.workOrder="{ item }">
          <div v-if="item.work_order" class="d-flex align-center justify-center">
            <show-work-order :work_order_id="item.work_order" class="OT-link font-weight-black">
              #{{ item.work_order }}
            </show-work-order>
          </div>
          <v-icon v-else small color="grey lighten-3">fas fa-minus</v-icon>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-menu offset-y left transition="slide-y-transition" rounded="lg">
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small v-bind="attrs" v-on="on">
                <v-icon small>fas fa-ellipsis-v</v-icon>
              </v-btn>
            </template>
            <v-list dense class="py-1 bespoke-menu">
              <!-- Primary Detail View -->
              <v-list-item @click.stop="showSalesNote(item)" class="px-3 py-1">
                <v-list-item-icon class="mr-3 ml-0"><v-icon small color="blue-grey lighten-1">fas fa-eye</v-icon></v-list-item-icon>
                <v-list-item-title class="body-2 font-weight-bold">Ver Detalles</v-list-item-title>
              </v-list-item>

              <v-divider class="my-1"></v-divider>

              <v-list-item v-if="!item.work_order" @click.stop="approveSalesNote(item)" :disabled="item.status === 'rejected'" class="px-3 py-1">
                <v-list-item-icon class="mr-3 ml-0"><v-icon small color="success">fas fa-check-double</v-icon></v-list-item-icon>
                <v-list-item-title class="body-2 font-weight-bold success--text">Confirmar Negocio</v-list-item-title>
              </v-list-item>
              
              <v-list-item 
                @click.stop="edit(item)" 
                :disabled="item.work_order ? !$can('update_with_OT', 'SalesNote') : !$can('update', 'SalesNote') || item.status == 'rejected'"
                class="px-3 py-1"
              >
                <v-list-item-icon class="mr-3 ml-0"><v-icon small color="primary">fas fa-edit</v-icon></v-list-item-icon>
                <v-list-item-title class="body-2 font-weight-medium">Modificar</v-list-item-title>
              </v-list-item>

              <v-list-item @click.stop="generateQuotation(item)" class="px-3 py-1">
                <v-list-item-icon class="mr-3 ml-0"><v-icon small color="blue-grey">fas fa-file-pdf</v-icon></v-list-item-icon>
                <v-list-item-title class="body-2 font-weight-medium">PDF Cliente</v-list-item-title>
              </v-list-item>

              <v-list-item v-if="$can('readCost', 'SalesNote')" @click.stop="generateCostReport(item)" class="px-3 py-1">
                <v-list-item-icon class="mr-3 ml-0"><v-icon small color="primary">fas fa-calculator</v-icon></v-list-item-icon>
                <v-list-item-title class="body-2 font-weight-medium">Analizar Costos</v-list-item-title>
              </v-list-item>

              <v-list-item v-if="$can('duplicate', 'SalesNote')" @click.stop="duplicate(item)" class="px-3 py-1">
                <v-list-item-icon class="mr-3 ml-0"><v-icon small color="primary">fas fa-copy</v-icon></v-list-item-icon>
                <v-list-item-title class="body-2 font-weight-medium">Duplicar Cotización</v-list-item-title>
              </v-list-item>

              <v-divider class="my-1"></v-divider>

              <v-list-item v-if="!item.work_order && item.status == 'pending' && $can('reject', 'SalesNote')" @click="reject(item)" class="px-3 py-1">
                <v-list-item-icon class="mr-3 ml-0"><v-icon small color="error">fas fa-times-circle</v-icon></v-list-item-icon>
                <v-list-item-title class="body-2 font-weight-medium error--text">Rechazar Oportunidad</v-list-item-title>
              </v-list-item>

              <v-list-item 
                v-if="!item.work_order && item.status != 'rejected' && $can('delete', 'SalesNote')" 
                @click.stop="() => { salesNotetoDelete = item; showDeleteSalesNote = true; }"
                class="px-3 py-1"
              >
                <v-list-item-icon class="mr-3 ml-0"><v-icon small color="grey darken-1">fas fa-trash-alt</v-icon></v-list-item-icon>
                <v-list-item-title class="body-2 font-weight-medium grey--text text--darken-3">Eliminar</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </v-card>

    <!-- Modals -->
    <confirm
      v-if="salesNotetoDelete"
      v-model="showDeleteSalesNote"
      :message="`¿Desea eliminar permanentemente la cotización # ${salesNotetoDelete.id}? Esta acción no se puede deshacer.`"
      @ok="removeSalesNote(salesNotetoDelete)"
      @cancel="showDeleteSalesNote = false"
    />
    
    <v-dialog v-model="displaySalesNote" max-width="1300">
      <v-sheet class="rounded-xl overflow-hidden shadow-xl">
        <v-card flat>
          <SalesDetail v-if="displaySalesNote" :salesNote="salesNoteOnDisplay" @close="displaySalesNote = false" />
        </v-card>
      </v-sheet>
    </v-dialog>

    <v-dialog v-model="approvingSalesNote" max-width="1200" persistent>
      <v-card class="rounded-xl overflow-hidden">
        <approve-sales-note :salesNote="salesToApprove" @close="fetch" />
      </v-card>
    </v-dialog>

    <v-dialog v-model="rejectingSalesNote" max-width="600" persistent>
      <v-card class="rounded-xl overflow-hidden">
        <reject-sales-note
          :salesNote="salesToReject"
          @refresh="() => { rejectingSalesNote = false; salesToReject = {}; fetch(); }"
          @cancel="() => { rejectingSalesNote = false; salesToReject = {}; }"
        />
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { fetchSalesNote, deleteSalesNote } from "@/api/salesNotes";
import CustomerComplete2 from "@/components/customers/CustomerComplete2";
import ApproveSalesNote from "@/components/sales/ApproveSalesNote";
import RejectSalesNote from "@/components/sales/RejectSalesNote";
import notifications from "@/utils/notifications";
import { pdfQuotation } from "@/doc_generators/quotation";
import { pdfCostReport } from "@/doc_generators/costReport";
import SalesDetail from "@/components/sales/SalesDetail";
import ExportSalesNotes from "@/components/sales/ExportSalesNotes";
import OriginSelection from '@/components/sales/OriginSelection.vue'
import moment from "moment";
import store from "@/store";

export default {
  name: "SalesNotes",
  components: {
    CustomerComplete2,
    ApproveSalesNote,
    RejectSalesNote,
    SalesDetail,
    ExportSalesNotes,
    OriginSelection
  },
  data: () => ({
    loading: false,
    salesNotes: [],
    search: "",
    totalSalesNotes: 0,
    options: {
      sortBy: ['created_at'],
      sortDesc: [false]
    },
    filter: {
      created_after: moment().startOf('month').format("yyyy-MM-DD"),
      created_before: "",
      closing_after: "",
      closing_before: "",
      customer: "",
      status: "",
      seller: "",
      origin: "",
    },
    statusOptions: [
      { text: 'Abierta / Pendiente', value: 'pending' },
      { text: 'Ganada / Confirmada', value: 'confirmed' },
      { text: 'Cerrada / Rechazada', value: 'rejected' },
    ],
    sellers: [],
    displaySalesNote: false,
    salesNoteOnDisplay: {},
    approvingSalesNote: false,
    rejectingSalesNote: false,
    salesToReject: {},
    salesToApprove: {},
    quoted: 0,
    confirmed: 0,
    rejected: 0,
    showDeleteSalesNote: false,
    salesNotetoDelete: null,
    timer: null,
  }),
  computed: {
    headers() {
      return [
        { text: "N°", align: "start", value: "id", class: "table-header-custom", width: "80" },
        { text: "Creada", align: "start", value: "created_at", class: "table-header-custom", width: "120" },
        { text: "Proyecto / Referencia", align: "start", value: "name", class: "table-header-custom" },
        { text: "Cliente / Entidad", align: "start", value: "customer__name", class: "table-header-custom" },
        { text: "Responsable", align: "start", value: "customer__seller__first_name", class: "table-header-custom", sortable: false },
        { text: "Compromiso", align: "start", value: "closing_date", class: "table-header-custom", width: "120" },
        { text: "Total Neto", align: "end", value: "total", class: "table-header-custom", sortable: false, width: "160" },
        { text: "OT", align: "center", value: "workOrder", class: "table-header-custom", sortable: false, width: "80" },
        { text: "", align: "center", value: "actions", sortable: false, class: "table-header-custom", width: "50" },
      ];
    },
  },
  methods: {
    fromNow(date) {
      if (!date) return ''
      return moment(date).fromNow()
    },
    isUrgent(item) {
      if (item.status !== 'pending' || !item.closing_date) return false
      return moment().isAfter(moment(item.closing_date).subtract(2, 'days'))
    },
    create() {
      this.$router.push({ name: "SalesNoteCreateUpdate" });
    },
    edit(item) {
      this.$router.push({ name: "SalesNoteCreateUpdate", params: { id: item.id } });
    },
    duplicate(item) {
      this.$router.push({ name: "SalesNoteCreateUpdate", params: { id: item.id, duplicate: true } });
    },
    generateQuotation(item) {
      pdfQuotation(item);
    },
    generateCostReport(item) {
      pdfCostReport(item);
    },
    reject(item) {
      this.salesToReject = item;
      this.rejectingSalesNote = true;
    },
    async fetch() {
      this.loading = true;
      this.approvingSalesNote = false; // Cierra el modal de aprobación
      
      let created_after = "";
      let created_before = "";
      let closing_after = "";
      let closing_before = "";
      
      if (this.filter.created_after) created_after = moment(this.filter.created_after).utc().format("yyyy-MM-DD HH:mm");
      if (this.filter.created_before) created_before = moment(this.filter.created_before).utc().add(1439, "minutes").format("yyyy-MM-DD HH:mm");
      if (this.filter.closing_after) closing_after = moment(this.filter.closing_after).format("yyyy-MM-DD");
      if (this.filter.closing_before) closing_before = moment(this.filter.closing_before).format("yyyy-MM-DD");

      const { sortBy, sortDesc, page, itemsPerPage } = this.options;
      
      if (!this.$can("readAll", "SalesNote")) {
        const user = this.$store.getters.getUser;
        this.filter.seller = user.id;
      }

      try {
        const data = await fetchSalesNote(
          sortBy[0] ? (sortDesc[0] ? `${sortBy[0]}` : `-${sortBy[0]}`) : "-created_at",
          this.search,
          created_after,
          created_before,
          closing_after,
          closing_before,
          this.filter.customer ? this.filter.customer.id : '',
          this.filter.seller || '',
          '',
          page || 1,
          itemsPerPage || 10,
          this.filter.status || '',
          this.filter.origin || ''
        );
        
        this.totalSalesNotes = data.count;
        this.salesNotes = this.processFlags(data.results);
        this.quoted = data.quoted;
        this.confirmed = data.confirmed;
        this.rejected = data.rejected;
      } catch (e) {
        notifications.showError("Error al cargar cotizaciones");
      } finally {
        this.loading = false;
      }
    },
    processFlags(results) {
      return results.map((item) => {
        let flag = false;
        let yellow_flag = false;
        if (item.totals.margin < 0) flag = true;
        const { products } = item.details;
        for (let i = 0; i < products.length; i++) {
          if (products[i].price_bellow_cost) flag = true;
          if (products[i].price_bellow_original || products[i].margin_bellow_original) yellow_flag = true;
        }
        item = { ...item, flag, yellow_flag };
        return item;
      });
    },
    showSalesNote(item) {
      this.salesNoteOnDisplay = item;
      this.displaySalesNote = true;
    },
    approveSalesNote(item) {
      this.salesToApprove = item;
      this.approvingSalesNote = true;
    },
    searchTimeOut() {
      if (this.timer) clearTimeout(this.timer);
      this.timer = setTimeout(() => { this.fetch(); }, 800);
    },
    async removeSalesNote(item) {
      this.loading = true;
      try {
        await deleteSalesNote(item.id);
        notifications.showSuccess(`Cotización # ${item.id} eliminada correctamente`);
        this.showDeleteSalesNote = false;
        this.fetch();
      } catch (e) {
        notifications.showError("Error al eliminar");
      } finally {
        this.loading = false;
      }
    },
  },
  watch: {
    filter: {
      deep: true,
      handler: "fetch",
    },
    options: {
      handler: "fetch",
      deep: true,
    },
  },
  async mounted() {
    this.sellers = this.$store.getters.getSellers;
    this.fetch();
  },
};
</script>

<style scoped>
.modern-sales-notes-view { background-color: #f0f2f5; min-height: 100vh; font-family: 'Outfit', sans-serif !important; }
.modern-card { border: 1px solid rgba(0,0,0,0.08) !important; border-radius: 12px !important; background: white; }
.shadow-sm { box-shadow: 0 4px 12px rgba(0,0,0,0.03) !important; }
.lh-1 { line-height: 1 !important; }

/* Bespoke Mini Cards */
.bespoke-mini-card { position: relative; background: white !important; border: 1px solid rgba(0,0,0,0.08) !important; border-radius: 12px !important; transition: all 0.3s ease; overflow: hidden; }
.metric-accent { position: absolute; top: 0; left: 0; right: 0; height: 3px; }
.blue-accent { background: #35BDC8; }
.success-accent { background: #4caf50; }
.red-accent { background: #f44336; }

.metric-label { font-size: 0.65rem; font-weight: 800; color: #94a3b8; text-transform: uppercase; letter-spacing: 1px; margin-bottom: 4px; }
.tracking-tighter { letter-spacing: -1px; }

/* Table Styling */
.modern-table >>> td { height: 72px !important; border-bottom: 1px solid rgba(0,0,0,0.03) !important; }
.modern-table >>> .v-data-table-header { background-color: #f8fbfc !important; }
.modern-table >>> th { text-transform: uppercase; font-size: 0.7rem !important; font-weight: 800 !important; color: #64748b !important; letter-spacing: 1px !important; }
.table-header-custom { background-color: #f8fbfc !important; height: 48px !important; }

.OT-link { cursor: pointer; color: var(--v-primary-base); text-decoration: none; border-bottom: 1px dashed var(--v-primary-base); }
.OT-link:hover { color: var(--v-accent-base); border-bottom-color: var(--v-accent-base); }

.gap-1 { gap: 4px; }
.gap-2 { gap: 8px; }

.bespoke-menu { border-radius: 12px !important; border: 1px solid rgba(0,0,0,0.1) !important; }
.shadow-xl { box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important; }

.h-44 { height: 44px !important; }
.italic { font-style: italic; }

/* Custom Link Style */
.bespoke-link {
  color: var(--v-primary-base);
  text-decoration: none;
  border-bottom: 2px solid transparent;
  transition: all 0.2s ease;
  cursor: pointer;
  display: inline-block;
}

.bespoke-link:hover {
  color: var(--v-accent-base);
  border-bottom-color: var(--v-accent-base);
}

.modern-table >>> tbody tr {
  transition: background-color 0.2s ease;
  cursor: zoom-in; /* Indicates more info on click */
}

.modern-table >>> tbody tr:hover {
  background-color: #f8fafc !important;
}

.pointer-cursor { cursor: pointer; }
</style>
